import React, { useState } from "react";
import { AlertCircle, CalendarDays, LockKeyhole } from "lucide-react";
import { bankTransferAutoConfirmApiService } from "../../../../infrastructure/api/company/bank-transfer-auto-confirm-api-service";
import type { AutoConfirmOverview } from "../../../../domain/types/bank-transfer-auto-confirm";
import { ModalShell, isUserCanceled, loadTossSdk } from "./shared";
import { money } from "../bankTransferFormat";

/**
 * 결제수단 관리.
 *
 * **화면에 늘 펼쳐두지 않는다.** 일상 업무는 계좌 상태를 보는 것이지 카드를 들여다보는
 * 게 아니라, ⋯ 메뉴 안에 넣고 필요할 때만 연다.
 *
 * 단 결제가 깨졌을 때는 다르다. 그때는 바깥 화면이 배너로 끌어올려 여기로 보낸다 —
 * 사장님이 손대야 풀리는 문제이고, 방치하면 유예가 끝나 계좌가 끊긴다.
 */
export const PaymentMethodModal: React.FC<{
  companyId: number;
  overview: AutoConfirmOverview;
  nextBillingAt?: string;
  nextBillingTotal: number;
  readOnly: boolean;
  onClose: () => void;
  onChanged: () => Promise<void> | void;
}> = ({ companyId, overview, nextBillingAt, nextBillingTotal, readOnly, onClose, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const registered = overview.billingMethod?.status === "ACTIVE";

  const change = async () => {
    if (readOnly || busy) return;
    setBusy(true);
    setError("");
    try {
      const prepared = await bankTransferAutoConfirmApiService.prepareBillingMethod(companyId);
      await loadTossSdk();
      if (!window.TossPayments) throw new Error("결제창을 열지 못했습니다. 잠시 후 다시 시도해 주세요.");
      await window
        .TossPayments(prepared.clientKey)
        .payment({ customerKey: prepared.customerKey })
        .requestBillingAuth({
          method: "CARD",
          successUrl: prepared.successUrl,
          failUrl: prepared.failUrl,
          customerName: "결제수단 등록",
        });
    } catch (e) {
      if (!isUserCanceled(e)) {
        setError(e instanceof Error ? e.message : "결제수단 등록을 시작하지 못했습니다.");
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (readOnly || busy) return;
    if (
      !window.confirm(
        "등록된 결제수단을 지울까요?\n\n" +
          "다시 계좌를 이용하시려면 카드를 새로 인증해야 합니다.\n" +
          "이용 중인 계좌가 있으면 지울 수 없습니다.",
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      await bankTransferAutoConfirmApiService.removeBillingMethod(companyId);
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "결제수단을 지우지 못했습니다.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell onClose={onClose}>
      <div className="w-full max-w-[520px] overflow-hidden rounded-[12px] bg-white">
        <div className="flex items-start justify-between px-6 pt-6">
          <div>
            <h3 className="text-[17px] font-bold text-[#222222]">결제수단 관리</h3>
            <p className="mt-1 text-[13px] text-[#888888]">
              계좌 구독은 등록된 카드로 자동 결제됩니다.
            </p>
          </div>
          <button onClick={onClose} className="text-[20px] leading-none text-[#888888]">
            ×
          </button>
        </div>

        <div className="px-6 py-5">
          {error && (
            <div className="mb-4 flex items-center gap-2 rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-3 py-2.5 text-[13px] text-[#4285F4]">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {/*
            마스킹 번호가 아니라 상태로 판단한다. 결제사가 카드 표시정보를 안 내려주는
            경우가 있는데, 그때도 자동결제는 정상 동작한다 — 번호로 판단하면
            "등록된 카드가 없습니다"라고 하면서 매달 결제되는 상태가 된다.
          */}
          {registered ? (
            <div className="flex items-center gap-4 border-b border-[#EEEEEE] pb-4">
              <span className="flex h-9 w-[52px] items-center justify-center rounded-lg bg-[#E8EFFF] text-[10px] font-bold text-[#3367D6]">
                CARD
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold text-[#222222]">
                  {overview.billingMethod?.cardCompany ?? "등록 카드"}{" "}
                  {overview.billingMethod?.cardNumberMasked ?? "등록 완료"}
                </div>
                <div className="mt-0.5 text-[12px] text-[#888888]">정기결제 기본 수단</div>
              </div>
              <span className="rounded px-2 py-1 text-[11px] font-medium bg-[#E8EFFF] text-[#4285F4]">
                사용 중
              </span>
            </div>
          ) : (
            <div className="rounded-[8px] bg-[#F5F5F5] px-4 py-4 text-sm text-[#888888]">
              등록된 카드가 없습니다. 계좌를 추가할 때 함께 등록하거나 지금 등록할 수 있어요.
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => void change()}
              disabled={readOnly || busy}
              className="rounded-lg border border-[#E0E0E0] px-4 py-2.5 text-[13px] font-medium text-[#555555] hover:bg-[#F5F5F5] disabled:text-[#BBBBBB]"
            >
              {registered ? "카드 변경" : "카드 등록"}
            </button>
            {registered && (
              <button
                onClick={() => void remove()}
                disabled={readOnly || busy}
                className="rounded-lg border border-[#E0E0E0] px-4 py-2.5 text-[13px] font-medium text-[#F24A4A] hover:bg-[#FEF2F2] disabled:text-[#BBBBBB]"
              >
                카드 지우기
              </button>
            )}
          </div>

          <div className="mt-4 flex items-start gap-2 text-[12px] leading-5 text-[#888888]">
            <LockKeyhole size={14} className="mt-0.5 shrink-0" />
            카드번호 전체·유효기간·CVC는 저장하지 않습니다. 마스킹된 번호와 카드사명만
            보관하고, 매달 결제는 결제사가 발급한 자동결제 등록정보로 요청합니다.
          </div>

          <div className="mt-5 rounded-[8px] border border-[#EEEEEE] bg-[#FAFAFA] px-4 py-3">
            <div className="flex items-center justify-between text-[13px]">
              <span className="flex items-center gap-1.5 text-[#888888]">
                <CalendarDays size={14} className="text-[#4285F4]" /> 다음 결제
              </span>
              <b className="text-[#222222]">
                {nextBillingAt
                  ? new Date(nextBillingAt).toLocaleDateString("ko-KR", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : "—"}
              </b>
            </div>
            <div className="mt-1 flex items-center justify-between text-[13px]">
              <span className="text-[#888888]">결제 예정액</span>
              <b className="text-[#222222]">{nextBillingTotal ? money(nextBillingTotal) : "—"}</b>
            </div>
          </div>
        </div>

        <div className="flex justify-end border-t border-[#EEEEEE] px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-[#E0E0E0] px-4 py-2.5 text-[13px] font-medium text-[#555555] hover:bg-[#F5F5F5]"
          >
            닫기
          </button>
        </div>
      </div>
    </ModalShell>
  );
};
